<script setup lang="ts">
import {
  ProductDescriptionSection,
  ProductStickyNavConfig,
} from "~/config/product";
import Product from "~/models/Product";
import ProductImage from "~/models/ProductImage";
import SafeHtml from "@/components/utils/SafeHtml.vue";
import AddToCartButton from "~/components/buttons/AddToCartButton.vue";
import { formatPrice } from "@/utils/pricing";
import {
  computed,
  nextTick,
  onBeforeMount,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import {
  breakpointsBootstrapV5,
  useBreakpoints,
  useWindowScroll,
  useWindowSize,
} from "@vueuse/core";

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
});
const emit = defineEmits<{
  isSticky: [value: boolean];
}>();

type NavItem = {
  icon: string;
  target: string;
  name: string;
  active?: boolean;
};

const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileSize = breakpoints.smallerOrEqual("md");
const isSticky = ref(false);
const stickyNav = ref<HTMLElement | null>(null);
const mobileContainer = ref<HTMLElement | null>(null);
const desktopContainer = ref<HTMLElement | null>(null);
const windowSize = useWindowSize();
const windowScroll = useWindowScroll();
const items = reactive<NavItem[]>([]);

const initTargetTop = ref(0);
const initOffsetLeftValue = ref(0);

onBeforeMount(() => {
  // Prepara los elementos del nav
  prepareItems();
});

onMounted(() => {
  mobileContainer.value = document.querySelector(
    `#${props.product.componentId} .${ProductStickyNavConfig.MobileContainerCssClass}`,
  );
  desktopContainer.value = document.querySelector(
    `#${props.product.componentId} .${ProductStickyNavConfig.DesktopContainerCssClass}`,
  );
  nextTick(() => {
    nextTick(() => {
      stickyNav.value = document.querySelector(
        `#${props.product.componentId} .sticky-product-nav`,
      );
      refreshPositions();
      refreshSticky();
    });
  });
});

watch([windowSize.width, windowScroll.y], () => {
  refreshPositions();
  refreshSticky();
});

const prepareItems = () => {
  const product = props.product;
  if (product.description || product.short_description) {
    items.push({
      icon: "bi-info-circle",
      target: `#${ProductDescriptionSection.Description}`,
      name: "Descripción",
    });
  }
  if (product.description_file || product.description_landing) {
    items.push({
      icon: "bi-file-earmark-richtext",
      target: `#${ProductDescriptionSection.Characteristics}`,
      name: "Características",
    });
  }
  if (Object.values(product.attribute_group_map).length) {
    items.push({
      icon: "bi-gear",
      target: `#${ProductDescriptionSection.Specifications}`,
      name: "Especificaciones",
    });
  }
  // if (product.applications.length) {
  //   items.push({
  //     icon: "bi-circle-square",
  //     target: `#${ProductDescriptionSection.Applications}`,
  //     name: "Aplicaciones",
  //   });
  // }
  // if (product.games.length) {
  //   items.push({
  //     icon: "bi-controller",
  //     target: `#${ProductDescriptionSection.Games}`,
  //     name: "Juegos",
  //   });
  // }
};

const refreshPositions = () => {
  if ((desktopContainer.value?.offsetTop || 0) > 0 && !isSticky.value) {
    initTargetTop.value = desktopContainer.value?.offsetTop || 0;
  }
  if (desktopContainer.value) {
    initOffsetLeftValue.value =
      desktopContainer.value.offsetLeft +
      (parseInt(desktopContainer.value.style.paddingLeft) || 0);
  }
};

const getOffset = () => {
  const stickyHeight = stickyNav.value?.offsetHeight || 0;
  const margin = 20; // permite agregar una distancia extra de detección a cada sección
  const offset = stickyHeight + margin;
  return offset;
};

const refreshSticky = () => {
  if (!stickyNav.value) {
    return;
  }
  const offset = getOffset();
  // Evalúa cuando la parte superior del viewport alcanza el nav para agregar los estilos correspondientes de sticky.
  const currentScrollTop = windowScroll.y.value;
  const stickyContainer = stickyNav.value.parentElement;
  if (!stickyContainer) {
    return;
  }
  // const targetEl = isMobileSize.value ? mobileTarget : desktopTarget;
  const targetOffset = isMobileSize.value ? 48 : 0;
  const targetTop = initTargetTop.value + targetOffset;
  const offsetLeft = initOffsetLeftValue.value;
  const newIsSticky = currentScrollTop >= targetTop;
  if (newIsSticky) {
    stickyContainer.classList.add("is-sticky");
    stickyContainer.style.marginLeft = `-${offsetLeft}px`;
    stickyContainer.style.paddingLeft = `${offsetLeft}px`;
    stickyContainer.style.paddingRight = `${offsetLeft}px`;
    stickyContainer.style.width = `${document.body.offsetWidth}px`;
  } else {
    stickyContainer.classList.remove("is-sticky");
    stickyContainer.style.marginLeft = "0px";
    stickyContainer.style.paddingLeft = "0px";
    stickyContainer.style.paddingRight = "0px";
    stickyContainer.style.width = "auto";
  }
  isSticky.value = newIsSticky;
  let activeItem: NavItem | null = null;
  let prevTop = 0;
  items.forEach((item: NavItem) => {
    item.active = false;
    const section = document.querySelector(item.target) as HTMLElement;
    if (!section) {
      return;
    }
    // Detecta cuando el scroll haya alcanzado una sección un pequeño offset para detectarlo antes de llegar.
    const currHalf = section.offsetHeight / 2;
    const currCenter = section.offsetTop + currHalf;
    const currDist = Math.abs(currCenter - currentScrollTop) - currHalf;
    if (currDist <= offset && (!prevTop || section.offsetTop > prevTop)) {
      activeItem = item as NavItem;
      prevTop = section.offsetTop;
    }
  });
  if (activeItem) {
    (activeItem as NavItem).active = true;
  }
  emit("isSticky", isSticky.value);
};

/** Ajusta el scroll para que se vea visible toda la sección omitiendo la altura del sticky nav. */
const fixScroll = (ev: Event, item: NavItem) => {
  const section = document.querySelector(item.target) as HTMLElement;
  if (!section) {
    return;
  }
  ev.preventDefault();
  const offset = getOffset() - 1;
  window.scrollTo({ top: section.offsetTop - offset });
};

const coverImage = computed(() => {
  return props.product
    .getImagesOfSize(ProductImage.Sizes.ExtraSmall, true)
    .at(0);
});
</script>

<template>
  <div :class="[ProductStickyNavConfig.MobileContainerCssClass]">
    <Teleport
      :to="`#${product.componentId} .${ProductStickyNavConfig.DesktopContainerCssClass}`"
      :disabled="isMobileSize && !isSticky"
    >
      <nav
        class="sticky-product-nav nav bg-white pt-2 pb-2 border-bottom gap-3 flex-nowrap"
      >
        <template v-if="!isMobileSize || !isSticky">
          <a
            v-for="item in items"
            :key="item.target"
            class="nav-link border px-5 py-3 link-info position-relative d-flex flex-column justify-content-center align-items-center"
            :class="{ active: item.active, 'nav-link-hovered': isMobileSize }"
            :href="item.target"
            @click="fixScroll($event, item)"
          >
            <i class="bi fs-3" :class="item.icon" />
            <span class="nav-text text-truncate mw-30 d-inline-block">{{
              item.name
            }}</span>
            <div class="nav-underline"></div>
          </a>
        </template>
        <div
          v-if="isSticky"
          class="nav-product d-flex gap-1 gap-md-4 align-items-center mx-auto me-md-0 ms-md-auto"
        >
          <a
            v-if="!isMobileSize"
            href="#"
            class="d-flex flex-column align-items-center link-secondary link-underline-opacity-0"
          >
            <i class="bi bi-arrow-up-short fs-3" />
            <small>{{ isMobileSize ? "Subir" : "Ir al principio" }}</small>
          </a>
          <a href="#" class="link-underline-opacity-0 me-1">
            <img
              v-if="coverImage"
              :src="coverImage"
              :alt="product.name || ''"
              class="nav-product-image"
              width="60"
              height="60"
            />
          </a>
          <a href="#" class="nav-product-title">
            <span
              class="fw-semibold"
              :class="[isMobileSize && isSticky ? 'small-sm' : 'small']"
              >{{ product.name }}</span
            >
          </a>
          <safe-html
            class="fw-bold text-info"
            :class="[
              isMobileSize && isSticky ? 'small me-1' : 'fs-5 fs-md-3 me-2',
            ]"
            :html="formatPrice(product.current_price_with_tax)"
          />
          <add-to-cart-button v-slot="slotProps" :product="product">
            <button
              class="btn btn-danger btn-sm rounded-0 px-4 py-2 lh-sm"
              @click="slotProps.click(1)"
            >
              {{
                isMobileSize && isSticky
                  ? " Agregar"
                  : " &nbsp;&nbsp;Agregar&nbsp;&nbsp;"
              }}
            </button>
          </add-to-cart-button>
        </div>
      </nav>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
@use "@/assets/scss/variables" as *;
@use "@/assets/scss/bootstrap-helpers" as *;

.nav {
  overflow: auto;
  .nav-link {
    overflow: hidden;
    font-size: $h6-font-size;
    @include media-breakpoint-up(md) {
      font-size: $h5-font-size;
    }
  }
  .nav-text {
    bottom: -100%;
    transition: all 0.2s;
    position: absolute;
    opacity: 0;
  }
  i {
    transition: all 0.2s;
  }
  .nav-underline {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 0.3rem;
    background-color: var(--bs-light);
    margin: 2px;
  }
  .nav-link.active {
    .nav-text {
      position: static;
      transition: none;
      opacity: 1;
    }
    i {
      display: none;
    }
    .nav-underline {
      background-color: var(--bs-info);
    }
  }
  .nav-link-hovered {
    .nav-text {
      display: inline;
      bottom: 15%;
      font-size: 0.825rem;
      opacity: 1;
    }
    i {
      transform: translateY(-30%);
    }
  }
  .nav-link:not(.active):hover {
    @extend .nav-link-hovered;
  }
  .nav-product-image {
    width: auto;
    height: 60px;
  }
  .nav-product-title {
    text-decoration: none;
    span {
      display: -webkit-box;
      -webkit-box-orient: vertical;
      line-clamp: 3;
      -webkit-line-clamp: 3;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 300px;
      @include media-breakpoint-up(md) {
        line-clamp: 2;
        -webkit-line-clamp: 2;
      }
    }
  }
}
</style>
